<template>
  <div class="page-container">
    <div class="section">
      <section class="center-align margin-bottom">
        <h1 class="text-4xl font-bold margin-bottom">Under the Hood of Deploy10</h1>
        <p class="bigger-font text-muted-foreground">
          Designed for developers and DevOps: see how Deploy10 builds, routes, and scales your app across regions.
        </p>
      </section>

      <section class="feature margin-top-75">
        <h2 class="text-2xl font-semibold margin-bottom">🌍 Global Swarm Architecture</h2>
        <p class="margin-bottom">
          Deploy10 runs on a globally distributed network of <strong>Docker Swarm clusters</strong>. Each cluster is made of several physical servers (bare metal, rented from OVH), grouped by region (France, Germany, Canada, etc).
        </p>
        <p class="margin-bottom">
          When you deploy, you pick the countries. We pick the least loaded cluster in each selected country and deploy your app there.
        </p>
        <p>
          <strong>Inside each cluster:</strong> Swarm handles container orchestration, service replication, and node failover. If one server crashes, the others take over — no downtime.
        </p>
      </section>

      <section class="feature margin-top-75">
        <h2 class="text-2xl font-semibold margin-bottom">🛰️ Smart Geo Routing</h2>
        <p class="margin-bottom">
          We place a geo-aware HTTP reverse proxy in front of your app. Users are automatically routed to the closest available region (among the ones you deployed to). This improves latency and availability — without any configuration.
        </p>
        <p>
          Want to stay out of the US for data reasons? No problem — just deploy to Canada and Mexico. We’ll keep US users close, without crossing borders.
        </p>
      </section>

      <section class="feature margin-top-75">
        <h2 class="text-2xl font-semibold margin-bottom">⚙️ Build & Deploy Pipeline</h2>
        <ul class="list-disc list-inside space-y-2">
          <li><strong>Step 1:</strong> You connect your GitHub repo.</li>
          <li><strong>Step 2:</strong> You enter some details about your app and we generate a production-grade Dockerfile.</li>
          <li><strong>Step 3:</strong> The image is built and pushed to a private registry.</li>
          <li><strong>Step 4:</strong> The image is deployed to all selected regions with Swarm services.</li>
        </ul>
      </section>

      <section class="feature margin-top-75">
        <h2 class="text-2xl font-semibold margin-bottom">💸 Efficient Billing</h2>
        <p class="margin-bottom">
          You only pay when your app is running. Pricing is per-minute and scales with memory usage. The smallest container (128MB RAM) costs ~1€/month if running 24/7.
        </p>
        <p>
          No extra costs for CPU usage — it’s shared across the cluster. Just choose your memory footprint and regions.
        </p>
      </section>

      <section class="feature margin-top-75">
        <h2 class="text-2xl font-semibold margin-bottom">🛡️ Resilience & Scaling</h2>
        <p class="margin-bottom">
          Swarm ensures automatic failover and service replication. Every service is deployed with:
        </p>
        <ul class="list-disc list-inside space-y-2">
          <li>✚ <strong>Health checks</strong> to detect and restart failed containers</li>
          <li>✚ <strong>Rolling updates</strong> to deploy without downtime</li>
          <li>✚ <strong>Internal service discovery</strong> via DNS in overlay networks</li>
          <li>✚ <strong>Multi-host networking</strong> for scalable communication</li>
        </ul>
      </section>

      <section class="section margin-top-75">
        <h2>Infrastructure Diagram</h2>
        <div class="diagram">
          <div class="diagram-step">User</div>
          <div class="arrow">→</div>
          <div class="diagram-step">Load Balancer</div>
          <div class="arrow">→</div>
          <div class="diagram-step">Docker Swarm Cluster</div>
          <div class="arrow">→</div>
          <div class="diagram-step">Service</div>
        </div>
        <p class="center-align margin-top">
          Deploy10 automatically routes traffic to healthy services across multiple regions with high availability.
        </p>
      </section>

      <section class="center-align margin-100">
        <h2 class="text-3xl font-semibold margin-bottom">🚀 Ready to Try?</h2>
        <p class="text-muted-foreground bigger-font margin-bottom">
          Connect your repo and deploy globally in 30 seconds. No YAML. No Kubernetes. No headache.
        </p>
        <router-link :to="{ name: user.email ? 'Deployments' : 'SignUp' }">
          <button class="cta">Start Now</button>
        </router-link>
      </section>
    </div>
  </div>
</template>


<script>

  export default {
    name: 'HowItWorks',
    components: {  },
    props: {

    },
    data() {
      return {

      }
    },
    created() {

    },
    computed: {
      user() {
        return this.$store.state.loggedUser;
      },
    },
    methods: {

    }
  }
</script>

<style scoped>
.page-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.section {
  margin-bottom: 3rem;
}

.feature p {
  margin-bottom: 1rem;
  line-height: 1.7;
}

ul {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-left: 1.25rem;
  max-width: 700px;
}

li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.comparison {
  margin-top: 2rem;
  width: 100%;
  border-collapse: collapse;
}

.comparison th,
.comparison td {
  border: 1px solid #ddd;
  padding: 0.75rem;
  font-size: 0.95rem;
  text-align: center;
}

.comparison tr:nth-child(even) {
  background-color: #fafafa;
}

.comparison td:first-child {
  font-weight: 500;
  text-align: left;
}

.cta {
  margin-top: 2rem;
}

.diagram {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  flex-direction: row;
  margin-top: 1.5rem;
}

.diagram-step {
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  font-weight: 500;
  min-width: 120px;
  text-align: center;
}

.arrow {
  font-size: 1.5rem;
  color: #888;
}
</style>
